/* «Фотосессия товара» (APP-SPEC-5 §12; пайплайн n8n product_photoshoot). Порядок блоков §12.1: шапка «· 30 C за кадр» →
   лоток фото товара (первое — «Главное», щит «сам товар не меняем») → лента «Наборы» («Свой референс» до 9, «Свой набор»,
   наборы категории и общие; чип категории и «Сцены N ›») → «Кто в кадре» в режиме модели → карточка «Ещё настройки»
   (Формат · Сколько фото · Площадка · Съёмка · Пожелания) → «Создать N фото · M C». Каждый блок — по полю формы. */
import { useEffect, useState } from 'react';
import { api } from '../../api';
import type { UserAvatar } from '../../api/people';
import { uiFormat, uiText } from '../../i18n';
import { ChoiceTiles } from '../../ui/ChoiceTiles';
import { studioFields, type StudioApp } from '../studio/studio-api';
import { artworkUrl } from '../studio/studio-art-model';
import { record } from '../studio/studio-content';
import { StudioSettings, type StudioSettingSlot } from '../studio/StudioFormParts';
import { openStudioTarget } from '../studio/studio-navigation';
import { StudioIcon } from '../studio/StudioIcon';
import { StudioPhotoTray } from '../studio/StudioPhotoTray';
import { StudioSheet } from '../studio/StudioSheet';
import { useStudioDocument } from '../studio/use-studio-document';
import { useStudioForm } from '../studio/use-studio-form';
import { useStudioPhotoPreviews } from '../studio/use-studio-photo-previews';
import { CreateFoot, RightsBox, ScreenHead, TextSlot } from '../shoot/kit/FormParts';
import { allowed, decode, encode, fieldOf, valueOf, limitOf, orderPrice, photoSlots, plural, referenceSlots, shootSummary, unitPrice, writeField } from '../shoot/kit/form-model';
import { OrderTail } from '../shoot/kit/OrderTail';
import { PeoplePicker } from '../shoot/kit/PeoplePicker';
import { selfFace } from '../shoot/kit/people-model';
import { GridSheet, RailCard, type RailItem } from '../shoot/kit/Rail';
import { ReferenceCard, ReferencePlaque } from '../shoot/kit/ReferenceCard';
import { useReference } from '../shoot/kit/use-reference';
import { ShootingBlock } from '../shoot/kit/ShootingBlock';
import { usePeopleData } from '../shoot/kit/use-people-data';
import { modelShots, pshootKeys, pshootMissing, pshootScreen, splitScenes, type Scene } from './pshoot-model';
import '../shoot/shoot-native.css';
import './pshoot-native.css';

const OWN = 'own';
const FORMAT_NAMES: Record<string, [string, string]> = {
  mp: ['Как у площадки', ''],
  sq: ['Квадрат', 'Для соцсетей и Ozon Fresh'],
  p45: ['Пост', 'Для ленты соцсетей'],
  st: ['Сторис', 'На весь экран телефона'],
};
const photosWord = (count: number) => plural(count, uiText('фото'), uiText('фото'), uiText('фото'));

export function PShootOrderForm({ app, avatar, initialInputs }: { app: StudioApp; avatar: UserAvatar | null; initialInputs?: Record<string, unknown> }) {
  const state = useStudioForm({ app, avatar, initialInputs });
  const { form, inputs, locked } = state;
  const screenDoc = useStudioDocument(signal => api.request<Record<string, unknown>>('/app/studio/screens/pshoot', { signal }), 'pshoot-screen');
  const data = record(screenDoc.data);
  const screen = pshootScreen(data);
  const keys = pshootKeys(form);
  const people = usePeopleData(!!keys.avatarId);
  const [sheet, setSheet] = useState<'scenes' | 'category' | { scene: Scene } | null>(null);
  const [hint, setHint] = useState('');
  const photos = photoSlots(form).slice(0, 5);
  const previews = useStudioPhotoPreviews(state, photos);
  const reference = useReference(state, referenceSlots(form).slice(0, 9), setHint);
  const set = (key: string | undefined, value: unknown) => key && writeField(state.setInputs, form, key, value);

  const category = valueOf(form, inputs, keys.category) || screen.categories[0]?.id || '';
  const categoryName = screen.categories.find(item => item.id === category)?.name ?? '';
  const sets = screen.sets.filter(item => !item.category || item.category === category).slice(0, 12);
  const setId = valueOf(form, inputs, keys.set);
  const ownScenes = decode<Array<{ scene_id: string; count: number }>>(keys.scenes ? inputs[keys.scenes] : undefined, []).map(item => item.scene_id);
  const chosenSet = sets.find(item => item.id === setId);
  const sceneIds = reference.on ? [] : setId === OWN ? ownScenes : chosenSet?.scenes ?? [];
  const countField = keys.count ? fieldOf(form, keys.count) : undefined;
  const counts = countField?.values.length ? countField.values.map(Number).filter(Number.isFinite) : screen.counts;
  // Умолчание формы (docs/59 §4: у count — default_value 4) важнее первого числа списка: то же число уходит в заказ.
  const countSpec = record(studioFields(form).find(field => field.key === keys.count));
  const preset = Number(countSpec.default_value);
  const fallback = Number.isInteger(preset) && preset > 0 && (!counts.length || counts.includes(preset)) ? preset : counts[0] || 4;
  const count = reference.on ? reference.stored.length : Number(valueOf(form, inputs, keys.count)) || fallback;
  const distribution = splitScenes(sceneIds, count);
  const unit = unitPrice(form, data, 'pshoot');
  const price = form ? orderPrice(form, keys.count || reference.on ? unit : null, count) : { total: 0, confirmed: true };
  const platform = valueOf(form, inputs, keys.platform) || screen.platforms[0]?.id || '';
  const platformInfo = screen.platforms.find(item => item.id === platform);

  /* Число кадров и категория, которые экран уже показывает («Создать 4 фото», чип категории), уходят и в заказ:
     без count сервер снимет столько кадров, сколько задано шаблоном, без category не узнает категорию товара.
     В режиме референса кадров столько, сколько референсов. Замороженную заявку и страницу заказа не трогаем. */
  const countInput =
    countField &&
    count >= (typeof countSpec.min === 'number' ? countSpec.min : 1) &&
    count <= (typeof countSpec.max === 'number' ? countSpec.max : Infinity) &&
    allowed(countField, String(count))
      ? encode(countField, count)
      : undefined;
  const categoryField = keys.category ? fieldOf(form, keys.category) : undefined;
  const categoryInput = screen.categories.length && category && allowed(categoryField, category) ? encode(categoryField, category) : undefined;
  const pinCount = reference.on;
  const countKey = keys.count;
  const categoryKey = keys.category;
  const { orderId, setInputs } = state;
  useEffect(() => {
    if (!form || locked || orderId) return;
    setInputs(current => {
      let next = current;
      const put = (key: string | undefined, value: string | undefined, replace: boolean) => {
        if (key && value !== undefined && (replace ? current[key] !== value : !current[key])) next = { ...next, [key]: value };
      };
      put(countKey, countInput, pinCount);
      put(categoryKey, categoryInput, false);
      return next;
    });
  }, [form, locked, orderId, countKey, countInput, pinCount, categoryKey, categoryInput, setInputs]);

  /* Сцены и число кадров — производные набора: пишутся вместе с выбором, не догадкой при отправке */
  const applyScenes = (scenes: string[], nextCount = count) => keys.scenes && set(keys.scenes, splitScenes(scenes, nextCount));
  const chooseSet = (id: string) => {
    if (!keys.set) return;
    reference.select(false);
    set(keys.set, id);
    const scenes = id === OWN ? ownScenes : sets.find(item => item.id === id)?.scenes ?? [];
    applyScenes(scenes);
    const hasPeople = scenes.some(sceneId => screen.scenes.find(scene => scene.id === sceneId)?.people);
    if (hasPeople && keys.model && (valueOf(form, inputs, keys.model) || 'none') === 'none' && fieldOf(form, keys.model)?.values.includes('gen')) {
      set(keys.model, 'gen');
      setHint(uiText('В наборе есть сцены с моделью: поставили «Модель ИИ»'));
    }
  };
  const toggleOwnScene = (id: string) => {
    const next = ownScenes.includes(id) ? ownScenes.filter(value => value !== id) : [...ownScenes, id];
    if (next.length > screen.maxScenes) return setHint(uiFormat('В своём наборе до {0} сцен', screen.maxScenes));
    reference.select(false);
    set(keys.set, OWN);
    applyScenes(next);
  };

  /* Модель: none · me · person · char · gen (§9.2); «Вы» уходит тем, что стоит за «Вы» в аккаунте */
  const model = valueOf(form, inputs, keys.model) || 'none';
  const modelValue = model === 'person' ? `person:${valueOf(form, inputs, keys.personId)}` : model === 'char' ? `char:${valueOf(form, inputs, keys.avatarId)}` : model;
  const me = selfFace(people, avatar);
  const pickModel = (value: string) => {
    if (!keys.model) return;
    const [kind, id] = value.split(':');
    const next = modelValue === value && value !== 'none' ? 'none' : kind;
    state.setInputs(current => {
      const result = { ...current };
      const put = (key: string | undefined, value: unknown) => {
        if (!key) return;
        const encoded = encode(fieldOf(form, key), value);
        if (encoded === undefined) delete result[key];
        else result[key] = encoded;
      };
      put(keys.model, next);
      put(keys.personId, next === 'person' ? id : undefined);
      put(keys.avatarId, next === 'char' ? id : undefined);
      put(keys.me, next === 'me' ? (people.self?.src === 'avatar' && people.self.pick ? { src: 'avatar', avatar_id: Number(people.self.pick) } : { src: 'photos' }) : undefined);
      return result;
    });
    const scenesWithPeople = sceneIds.some(sceneId => screen.scenes.find(scene => scene.id === sceneId)?.people);
    if (next === 'none' && scenesWithPeople && keys.set) {
      const catalog = sets.find(item => !item.scenes.some(sceneId => screen.scenes.find(scene => scene.id === sceneId)?.people));
      if (catalog) chooseSet(catalog.id);
    }
  };
  const withModel = modelShots(screen, distribution);
  const modelName =
    model === 'none'
      ? ''
      : model === 'gen'
        ? uiText('Модель ИИ')
        : model === 'me'
          ? uiText('Вы')
          : model === 'person'
            ? people.persons.find(item => String(item.id) === valueOf(form, inputs, keys.personId))?.name ?? ''
            : people.characters.find(item => String(item.id) === valueOf(form, inputs, keys.avatarId))?.name ?? '';
  const modelCaption = reference.on
    ? modelName
      ? uiFormat('{0} — в кадрах по вашим фото', modelName)
      : 'Только свои фото или с согласием человека на фото'
    : !withModel
      ? 'В наборе нет сцен с людьми: модель не понадобится'
      : modelName
        ? uiFormat('{0} в {1} из {2}, остальные только с товаром', modelName, plural(withModel, uiText('кадре'), uiText('кадрах'), uiText('кадрах')), count)
        : 'Только свои фото или с согласием человека на фото';
  const modelNeedsPhoto =
    (model === 'me' && !me.ready) || (model === 'person' && !people.persons.find(item => String(item.id) === valueOf(form, inputs, keys.personId))?.photos.length);

  const sceneItem = (scene: Scene): RailItem => ({
    id: scene.id,
    name: scene.name,
    image: scene.image,
    themes: scene.themes,
    badge: scene.hit ? 'Хит' : scene.isNew ? 'Новое' : undefined,
    popularity: scene.popularity,
    isNew: scene.isNew,
    search: scene.description,
  });
  const setItem = (item: (typeof sets)[number]): RailItem => {
    const scenes = item.scenes.map(id => screen.scenes.find(scene => scene.id === id)).filter((scene): scene is Scene => !!scene);
    return {
      id: item.id,
      name: item.name,
      images: scenes.map(scene => scene.image).filter((url): url is string => !!url),
      badge: item.hit ? 'Хит' : item.isNew ? 'Новое' : undefined,
      count: setId === item.id ? photosWord(count) : undefined,
    };
  };
  const wishField = keys.wishes ? fieldOf(form, keys.wishes) : undefined;
  const wishLimit = limitOf(wishField, keys.wishes === 'wishes' ? 300 : 2000);
  const formatField = keys.format ? fieldOf(form, keys.format) : undefined;
  const formats = formatField?.values.length ? formatField.values : screen.formats.map(item => item.id);
  const formatLabel = (value: string) => FORMAT_NAMES[value]?.[0] ?? screen.formats.find(item => item.id === value)?.name ?? value;
  const formatSub = (value: string) =>
    value === 'mp'
      ? [platformInfo?.size, platformInfo ? uiFormat('под {0}', platformInfo.name) : ''].filter(Boolean).join(' · ')
      : FORMAT_NAMES[value]?.[1] ?? screen.formats.find(item => item.id === value)?.sub ?? '';
  const slots: StudioSettingSlot[] = [
    ...(keys.format && formats.length ? [{ title: 'Формат', value: valueOf(form, inputs, keys.format) && valueOf(form, inputs, keys.format) !== 'mp' ? formatLabel(valueOf(form, inputs, keys.format)) : 'По площадке' }] : []),
    ...(keys.count ? [{ title: 'Сколько фото', value: photosWord(count) }] : []),
    ...(keys.platform && screen.platforms.length ? [{ title: 'Площадка', value: platformInfo?.short || platformInfo?.name || '' }] : []),
    ...(keys.shoot ? [{ title: 'Съёмка', value: shootSummary(inputs.shoot) }] : []),
    ...(wishField && !wishField.required ? [{ title: 'Пожелания', value: valueOf(form, inputs, keys.wishes) || 'Добавить' }] : []),
  ];
  const missing = form
    ? pshootMissing({
        form,
        inputs,
        photoFields: photos.map(slot => slot.key),
        rights: state.rights,
        reference: reference.on,
        referenceChecking: reference.checking,
        scenes: sceneIds.length,
        modelNeedsPhoto,
        wishLimit,
        priceConfirmed: price.confirmed,
      })
    : null;
  const ready = photos.filter(slot => !!inputs[slot.key]).length;
  const main = photos.find(slot => previews.urls[slot.key]);

  return (
    <section className="native-studio-form shk-shoot shk-pshoot pf-in">
      {/* На странице заказа заголовок экрана не повторяется: название уже в шапке (кадры *-done) */}
      {!state.orderId && <ScreenHead title={app.name} unit={unit !== null ? uiFormat('{0} C за кадр', unit) : null} />}
      {!form && !state.error && <p role="status">{uiText('Загружаем…')}</p>}
      {(state.error || screenDoc.error) && (
        <p role="alert">
          {uiText(state.error || 'Не удалось загрузить сцены.')}{' '}
          <button
            type="button"
            disabled={state.busy}
            onClick={() => {
              if (screenDoc.error) screenDoc.reload();
              if (state.error) state.setRevision(value => value + 1);
            }}
          >
            {uiText('Повторить')}
          </button>
        </p>
      )}
      {form && !state.orderId && (
        <form
          onSubmit={event => {
            event.preventDefault();
            if (!state.busy && !state.submissionBlocked && (state.intent || (!missing && form.available))) void state.submit();
          }}
        >
          {!ready && screen.example.before && screen.example.after && (
            <div className="shk-example" aria-label={uiText('Пример: ваше фото и кадр в сцене')}>
              {artworkUrl(screen.example.before) && <img src={artworkUrl(screen.example.before)!} alt={uiText('Ваше фото')} />}
              {artworkUrl(screen.example.after) && <img src={artworkUrl(screen.example.after)!} alt={uiText('В сцене')} />}
            </div>
          )}
          {!!photos.length && (
            <StudioPhotoTray
              state={state}
              slots={photos}
              urls={previews.urls}
              minimum={1}
              title="Фото товара"
              description="От одного до пяти ракурсов, первое — главное"
              consent={<RightsBox checked={state.rights} disabled={locked} text="Подтверждаю право использовать фото товара" onChange={state.setRights} />}
            />
          )}
          {ready > 0 && (
            <p className="bc-cap shk-cap">
              <StudioIcon kind="eye" />
              {main ? `${uiText('Главное')} — ${uiText('первое фото')} · ` : ''}
              {uiText('Сам товар не меняем: только фон, свет и тени')}
            </p>
          )}

          {(keys.set || reference.slots.length > 0) && (
            <div className="bcs shk-rail">
              <div className="glbl bcs-gl shk-kits-h">
                {uiText('Наборы')}
                {keys.category && !!screen.categories.length && (
                  <button type="button" className="chip" disabled={locked} onClick={() => setSheet('category')}>
                    {categoryName} · {uiText('изменить')}
                  </button>
                )}
                {!!screen.scenes.length && keys.scenes && (
                  <button type="button" className="lnk" disabled={locked} onClick={() => setSheet('scenes')}>
                    {uiText('Сцены')} <span className="mono">{screen.scenes.filter(scene => !scene.category || scene.category === category).length}</span> ›
                  </button>
                )}
              </div>
              <div className="bcs-r nosb" role="group" aria-label={uiText('Наборы')}>
                {!!reference.slots.length && (
                  <ReferenceCard
                    state={state}
                    model={reference}
                    title="Загрузить фото"
                    onSelected={() => {
                      if (keys.set) set(keys.set, 'ref');
                      if (keys.scenes) set(keys.scenes, []);
                    }}
                    onDeselected={() => keys.set && set(keys.set, sets[0]?.id ?? '')}
                  />
                )}
                {keys.set && keys.scenes && (
                  <RailCard
                    item={{
                      id: OWN,
                      name: uiText('Свой набор'),
                      images: ownScenes.map(id => screen.scenes.find(scene => scene.id === id)?.image).filter((url): url is string => !!url),
                      count: setId === OWN ? photosWord(count) : undefined,
                    }}
                    on={setId === OWN && !reference.on}
                    locked={locked}
                    onPick={() => (ownScenes.length ? chooseSet(OWN) : setSheet('scenes'))}
                    className={ownScenes.length ? '' : 'dash'}
                  >
                    <span className="bcs-no">
                      <StudioIcon kind="plus" />
                      <small>{uiText('Выбрать сцены')}</small>
                    </span>
                  </RailCard>
                )}
                {keys.set &&
                  sets.map(item => (
                    <RailCard key={item.id} item={setItem(item)} on={setId === item.id && !reference.on} locked={locked} onPick={() => chooseSet(item.id)} />
                  ))}
              </div>
              <ReferencePlaque state={state} model={reference} shield="Товар снимем в таких же сценах: фон, свет, подача. Сам товар не меняем" />
            </div>
          )}
          {hint && <p role="status">{hint}</p>}

          {keys.model && (
            <PeoplePicker
              data={people}
              avatar={avatar}
              mode="single"
              selected={[modelValue]}
              limit={1}
              locked={locked}
              onToggle={pickModel}
              allowPets={false}
              withCharacters={!!keys.avatarId}
              caption={modelCaption}
              extra={[
                { value: 'none', name: uiText('Без модели'), icon: 'box', place: 'first' as const },
                ...(fieldOf(form, keys.model)?.values.includes('gen') ? [{ value: 'gen', name: uiText('Модель ИИ'), icon: 'sparkle', place: 'last' as const }] : []),
              ].filter(face => !fieldOf(form, keys.model!)?.values.length || fieldOf(form, keys.model!)!.values.includes(face.value))}
            />
          )}

          {wishField?.required && (
            <TextSlot
              label="Пожелания"
              value={valueOf(form, inputs, keys.wishes)}
              limit={wishLimit}
              disabled={locked}
              chips={screen.wishes}
              optional={false}
              note="Без текста на фото"
              onChange={value => set(keys.wishes, value)}
            />
          )}

          {!!slots.length && (
            <StudioSettings slots={slots} disabled={locked}>
              {keys.format && !!formats.length && (
                <ChoiceTiles
                  label={uiText('Формат')}
                  variant="rows"
                  value={valueOf(form, inputs, keys.format) || formats[0]}
                  disabled={locked}
                  onChange={value => set(keys.format, value)}
                  choices={formats.map(value => ({ value, label: uiText(formatLabel(value)), description: formatSub(value) }))}
                />
              )}
              {keys.count &&
                (reference.on ? (
                  <button type="button" className="sc-r off" aria-disabled="true" onClick={() => setHint(uiText('Сколько референсов, столько фото. Цена за кадр та же'))}>
                    <span className="m">
                      <b>{uiText('Сколько фото')}</b>
                      <small>{photosWord(count)}</small>
                    </span>
                  </button>
                ) : (
                  <ChoiceTiles
                    label={`${uiText('Сколько фото')} · ${uiText('поровну на сцены')}`}
                    variant="rows"
                    value={String(count)}
                    disabled={locked}
                    onChange={value => {
                      set(keys.count, value);
                      applyScenes(sceneIds, Number(value));
                    }}
                    choices={counts.map(value => ({
                      value: String(value),
                      label: photosWord(value),
                      description: unit !== null ? uiFormat('{0} C', unit * value) : undefined,
                    }))}
                  />
                ))}
              {keys.platform && !!screen.platforms.length && (
                <ChoiceTiles
                  label={uiText('Площадка')}
                  variant="rows"
                  value={platform}
                  disabled={locked}
                  onChange={value => set(keys.platform, value)}
                  choices={screen.platforms
                    .filter(item => !fieldOf(form, keys.platform!)?.values.length || fieldOf(form, keys.platform!)!.values.includes(item.id))
                    .map(item => ({ value: item.id, label: item.name, description: item.size }))}
                />
              )}
              {keys.shoot && <ShootingBlock state={state} styled={!!sceneIds.length} referenced={reference.on} />}
              {wishField && !wishField.required && (
                <TextSlot
                  label="Пожелания"
                  value={valueOf(form, inputs, keys.wishes)}
                  limit={wishLimit}
                  disabled={locked}
                  chips={screen.wishes}
                  note="Без текста на фото"
                  onChange={value => set(keys.wishes, value)}
                />
              )}
            </StudioSettings>
          )}

          <CreateFoot
            label={keys.count || reference.on ? uiFormat('Создать {0} · {1} C', photosWord(count), price.total) : uiFormat('Создать фото · {0} C', form.expected_price)}
            busy={state.busy}
            blocked={state.submissionBlocked}
            pending={!!state.intent}
            missing={missing}
            available={form.available}
            onCreate={() => void state.submit()}
          />
        </form>
      )}

      {sheet === 'category' && (
        <StudioSheet title="Категория товара" onClose={() => setSheet(null)}>
          <ChoiceTiles
            label={uiText('Категория товара')}
            variant="rows"
            value={category}
            disabled={locked}
            onChange={value => {
              set(keys.category, value);
              setSheet(null);
            }}
            choices={screen.categories.map(item => ({ value: item.id, label: item.name }))}
          />
        </StudioSheet>
      )}
      {sheet === 'scenes' && (
        <GridSheet
          title="Все сцены"
          items={screen.scenes.filter(scene => !scene.category || scene.category === category).map(sceneItem)}
          isOn={id => setId === OWN && ownScenes.includes(id)}
          onPick={item => toggleOwnScene(item.id)}
          onPeek={item => setSheet({ scene: screen.scenes.find(scene => scene.id === item.id)! })}
          locked={locked}
          themes={[...new Set(screen.scenes.flatMap(scene => scene.themes))]}
          tabs={[
            { id: 'all', label: uiText('Все'), filter: () => true },
            { id: 'pop', label: uiText('Популярные'), filter: item => (item.popularity ?? 0) > 0 },
            { id: 'sel', label: uiFormat('Выбранные · {0}', ownScenes.length), filter: item => ownScenes.includes(item.id) },
          ]}
          done={uiFormat('Готово · {0} · по {1}', plural(ownScenes.length, uiText('сцена'), uiText('сцены'), uiText('сцен')), photosWord(Math.max(1, Math.floor(count / Math.max(1, ownScenes.length)))))}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet && typeof sheet === 'object' && (
        <StudioSheet title="Сцена STEOS" onClose={() => setSheet('scenes')}>
          <div className="shk-scene">
            <h2>{sheet.scene.name}</h2>
            {artworkUrl(sheet.scene.image) && <img src={artworkUrl(sheet.scene.image)!} alt={sheet.scene.name} />}
            {!!sheet.scene.tags.length && <p className="shk-tags">{sheet.scene.tags.join(' · ')}</p>}
            {sheet.scene.description && <p>{sheet.scene.description}</p>}
            {!!sheet.scene.examples.length && (
              <>
                <h3>
                  {uiText('Примеры')} <span className="mono">{sheet.scene.examples.length}</span>
                </h3>
                <div className="shk-shots">
                  {sheet.scene.examples.map(url => (artworkUrl(url) ? <img key={url} src={artworkUrl(url)!} alt="" /> : null))}
                </div>
              </>
            )}
            {unit !== null && (
              <p>
                {uiText('О сцене')} · {uiFormat('{0} C за кадр', unit)}
              </p>
            )}
            <button
              type="button"
              className="btn"
              disabled={locked}
              onClick={() => {
                toggleOwnScene(sheet.scene.id);
                setSheet('scenes');
              }}
            >
              {setId === OWN && ownScenes.includes(sheet.scene.id) ? uiText('Убрать из набора') : uiFormat('Добавить в набор · {0}', photosWord(count))}
            </button>
          </div>
        </StudioSheet>
      )}

      <OrderTail
        state={state}
        kind="pshoot"
        groupLabel={reference.on ? 'По вашим фото' : app.name}
        copy={{
          running: ['Снимаем товар', 'Можно уйти с экрана: бот пришлёт фото в чат, как только набор будет готов.'],
          done: ['Фото товара готовы', platformInfo ? uiFormat('{0} под {1}. Бот уже прислал их в чат, в отдельную тему.', photosWord(count), platformInfo.name) : 'Бот уже прислал их в чат, в отдельную тему.'],
          failed: ['Фото товара не созданы', 'Проверьте пожелания и попробуйте ещё раз.'],
        }}
        card={[categoryName, platformInfo?.name, chosenSet?.name ?? (reference.on ? uiText('По вашим фото') : ''), modelName ? uiFormat('модель: {0}', modelName) : uiText('без модели')]
          .filter(Boolean)
          .join(' · ')}
        saveLabel="Скачать всё"
        failLabel="Изменить пожелания"
        limit={48}
        next={[
          { label: 'Слайды с преимуществами', icon: 'list', onClick: () => openStudioTarget({ screen: 'infog', ret: true }) },
          { label: 'Видео товара, 5 сек', icon: 'video', onClick: () => openStudioTarget({ screen: 'video', options: { mode: 'product' }, ret: true }) },
          {
            label: 'Ещё сцены',
            icon: 'grid',
            onClick: () =>
              void state.returnToForm().then(done => {
                if (done) setSheet('scenes');
              }),
          },
        ]}
      />
    </section>
  );
}
